import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../Overlay/OverlayController";

// Dimmed symbol alpha (calculated from tint 0x555555)
const DIMMED_ALPHA = 0.6;

// Unified dimming overlay for both main game and free spins
const DimReelSet = memo(
  forwardRef(({ symbolWidth, symbolHeight, dividerDimensions }, ref) => {
    const dimSpriteRef = useRef(null);
    const dimAnimRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reels (show overlay on non-winning symbols)
        dimReels: () => {
          if (!dimSpriteRef.current) return;

          // Cancel any existing animation
          if (dimAnimRef.current) {
            dimAnimRef.current.pause();
          }

          currentAlphaRef.current = DIMMED_ALPHA;

          dimAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: DIMMED_ALPHA,
            duration: 200,
            easing: "easeOutQuad",
          });
        },

        // Remove dimming (clear overlay)
        undimReels: () => {
          if (!dimSpriteRef.current) return;

          // Cancel any existing animation
          if (dimAnimRef.current) {
            dimAnimRef.current.pause();
          }

          currentAlphaRef.current = 0;

          dimAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: 0,
            duration: 60,
            easing: "easeInQuad",
          });
        },
      }),
      []
    );

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        // Restore the alpha without animation when dimensions change
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
    }, [symbolWidth, symbolHeight, dividerDimensions]);

    return (
      <Container name="dim_overlay" zIndex={2}>
        <Sprite
          ref={dimSpriteRef}
          texture={PIXI.Texture.WHITE}
          width={symbolWidth * Settings.REEL_COUNT}
          height={symbolHeight * Settings.SYMBOLS_VISIBLE}
          anchor={0.5}
          tint={OVERLAY_BACKGROUND_COLOR}
          alpha={0}
          name="dim_overlay_placeholder"
        />
      </Container>
    );
  })
);

DimReelSet.displayName = "DimReelSet";

export default DimReelSet;
